import { SAVEDSERVICE_ROUTES } from '../../../config/api-routes';
import instance from '../../../config/instance';

/**
 * Get saved services list with pagination and filtering
 * This API fetches saved services with advanced filtering and pagination
 * Replaces the Redux getSavedServiceApi action
 *
 * @param {object} params - Query parameters
 * @param {string} params.search - Search term
 * @param {array} params.serviceCategory - Array of service category filters
 * @param {array} params.vehicleMake - Array of vehicle make filters
 * @param {boolean} params.pagination - Enable pagination (default: true)
 * @param {number} params.page - Page number (default: 1)
 * @param {number} params.limit - Number of items per page
 * @param {string} params.sortBy - Sort field
 * @param {string} params.sortOrder - Sort order (ASC/DESC)
 * @returns {Promise} - Saved services data with pagination info
 */
export const getSavedServiceApi = async (params = {}) => {
    const queryParams = new URLSearchParams();

    // Add basic parameters to query string
    Object.keys(params).forEach((key) => {
        if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
            // Handle array parameters (filters)
            if (Array.isArray(params[key])) {
                params[key].forEach((item) => {
                    if (item !== undefined && item !== null && item !== '') {
                        queryParams.append(key, item);
                    }
                });
            } else {
                queryParams.append(key, params[key]);
            }
        }
    });

    // Handle specific array filters if passed separately (backward compatibility)
    // const { serviceCategory, vehicleMake, ...otherParams } = params;

    // // Add array filters if they exist
    // if (serviceCategory && Array.isArray(serviceCategory)) {
    //     serviceCategory.forEach((item) => queryParams.append('serviceCategory', item));
    // }
    // if (vehicleMake && Array.isArray(vehicleMake)) {
    //     vehicleMake.forEach((item) => queryParams.append('vehicleMake', item));
    // }

    // Add pagination params if not already added
    if (!queryParams.has('pagination')) {
        queryParams.append('pagination', 'true');
    }

    const queryString = queryParams.toString();
    const url = queryString ? `${SAVEDSERVICE_ROUTES.GET_SAVED_SERVICES}?${queryString}` : SAVEDSERVICE_ROUTES.GET_SAVED_SERVICES;

    const response = await instance.get(url);
    return response.data;
};

/**
 * Get saved service detail by ID
 * This API fetches detailed information about a specific saved service
 * @param {string} id - Saved service ID
 * @returns {Promise} - Saved service detail data
 */
export const getSavedServiceDetailApi = async (id) => {
    const response = await instance.get(SAVEDSERVICE_ROUTES.GET_SAVED_SERVICE(id));
    return response.data.data || response.data;
};

/**
 * Create new saved service
 * This API creates a new saved service with the provided details
 * @param {object} payload - Saved service data
 * @returns {Promise} - Created saved service data
 */
export const createSavedServiceApi = async (payload) => {
    const response = await instance.post(SAVEDSERVICE_ROUTES.CREATE_SAVED_SERVICE, payload);
    return response.data;
};

/**
 * Update existing saved service
 * This API updates an existing saved service with new details
 * @param {string} id - Saved service ID
 * @param {object} payload - Updated saved service data
 * @returns {Promise} - Updated saved service data
 */
export const updateSavedServiceApi = async (id, payload) => {
    const response = await instance.put(SAVEDSERVICE_ROUTES.UPDATE_SAVED_SERVICE(id), payload);
    return response.data;
};

/**
 * Delete saved service
 * This API deletes a saved service by ID
 * @param {string} id - Saved service ID
 * @returns {Promise} - Delete operation result
 */
export const deleteSavedServiceApi = async (id) => {
    const response = await instance.delete(SAVEDSERVICE_ROUTES.DELETE_SAVED_SERVICE(id));
    return response.data;
};

/**
 * Check if saved service already exists
 * This API checks if a saved service with the same service and vehicle make already exists
 * @param {object} params - Check parameters
 * @param {string} params._id - Saved service ID (for edit mode, to exclude current service)
 * @param {string} params.service - Service subcategory ID
 * @param {array} params.vehicleMake - Array of vehicle make IDs
 * @param {boolean} params.variant - Whether to check for variant (optional)
 * @returns {Promise} - Existing service data if found, false if not found
 */
export const checkSavedServiceExistanceApi = async (params) => {
    const response = await instance.post(SAVEDSERVICE_ROUTES.CHECK_SAVED_SERVICE, params);
    if (response.data.status) {
        return response.data.data;
    } else {
        return false;
    }
};
